import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { sendEmail, feedbackRequestEmail } from '../../lib/email';
import Modal from '../Modal';

// "Ask someone else" (post-event-feedback §4). The brief owner giving feedback
// isn't always the person who was closest to the job. This hands the one-minute
// form to a colleague: it emails them a link to the brief where the prompt is
// waiting. Nothing is answered on their behalf — whoever fills it in is recorded
// as answered_by_user_id when they submit, so the hand-off needs no new column.

const Spinner = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" className="animate-spin">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" fill="none" strokeOpacity="0.25" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" />
  </svg>
);

export default function BriefFeedbackAskModal({ brief, supplierName, currentUser, onSuccess, onClose }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [pickedId, setPickedId] = useState(null);
  const [message, setMessage] = useState('');
  const [state, setState] = useState('input'); // input | submitting | error
  const [errorMessage, setErrorMessage] = useState(null);
  const submitting = state === 'submitting';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('internal_users')
        .select('id, full_name, email, is_active')
        .order('full_name');
      if (cancelled) return;
      setUsers((data || []).filter(u => u.email && u.is_active !== false && u.id !== currentUser?.id));
    })();
    return () => { cancelled = true; };
  }, [currentUser?.id]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u => (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q));
  }, [users, search]);

  const picked = users.find(u => u.id === pickedId) || null;

  const submit = useCallback(async () => {
    if (!picked || !brief?.id || submitting) return;
    setState('submitting'); setErrorMessage(null);
    try {
      const briefUrl = `${window.location.origin}/briefs/${brief.id}`;
      const { subject, html } = feedbackRequestEmail({
        recipientName: picked.full_name,
        fromName: currentUser?.full_name || currentUser?.email || null,
        supplierName,
        briefName: brief.brief_name,
        message: message.trim() || null,
        briefUrl,
      });
      await sendEmail({ to: picked.email, subject, html });
      onSuccess?.({ name: picked.full_name || picked.email });
    } catch (e) {
      setErrorMessage(e.message || 'Could not send. Try again.');
      setState('error');
    }
  }, [picked, brief?.id, brief?.brief_name, supplierName, message, submitting, currentUser?.full_name, currentUser?.email, onSuccess]);

  const safeClose = () => { if (!submitting) onClose?.(); };

  return (
    <Modal open onClose={safeClose} maxWidth={520} stripe={false} ariaLabel="Ask someone else for feedback">
      <Modal.Header eyebrow="Ask someone else" title={supplierName || 'Supplier'} sub={brief?.brief_name} onClose={submitting ? undefined : safeClose} />
      <Modal.Body>
        <p className="font-['Metropolis'] text-[12px] text-[#51534A] mb-4 leading-snug">
          Not the right person to say how it went? Hand it to whoever was closest to the job — they'll get an email with a link to give the feedback here.
        </p>

        <label htmlFor="ask-search" className="block font-['Metropolis'] text-[13px] font-semibold text-ink mb-2">Who should give it?</label>
        <input
          id="ask-search"
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          disabled={submitting}
          placeholder="Search by name or email"
          className="w-full bg-white border border-line rounded-md px-3 py-2 font-['Metropolis'] text-[13px] text-ink placeholder:text-[#9BA08C] focus:outline-none focus:border-[#2F6E14] disabled:opacity-50 mb-2" />

        <div className="max-h-48 overflow-y-auto border border-line rounded-md divide-y divide-line">
          {filtered.length === 0 ? (
            <p className="font-['Metropolis'] text-[12px] text-[#51534A] px-3 py-3">No matching colleagues.</p>
          ) : filtered.map(u => {
            const on = u.id === pickedId;
            return (
              <button
                key={u.id}
                type="button"
                disabled={submitting}
                onClick={() => setPickedId(on ? null : u.id)}
                className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 transition-colors disabled:opacity-50 ${on ? 'bg-[#F5FAEE]' : 'bg-white hover:bg-[#F7F9EF]'}`}>
                <span className="min-w-0">
                  <span className="block font-['Metropolis'] text-[13px] font-semibold text-ink truncate">{u.full_name || u.email}</span>
                  {u.full_name && <span className="block font-['Metropolis'] text-[11px] text-[#51534A] truncate">{u.email}</span>}
                </span>
                {on && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2F6E14" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                )}
              </button>
            );
          })}
        </div>

        <label htmlFor="ask-message" className="block font-['Metropolis'] text-[13px] font-semibold text-ink mt-4 mb-2">
          Add a note <span className="text-[#9BA08C] text-[12px] font-normal">Optional</span>
        </label>
        <textarea
          id="ask-message"
          value={message}
          onChange={e => setMessage(e.target.value)}
          disabled={submitting}
          rows={3}
          placeholder="e.g. You ran the install — mind giving the feedback on these two?"
          className="w-full bg-white border border-line rounded-md px-3 py-2.5 font-['Metropolis'] text-[13px] text-ink placeholder:text-[#9BA08C] focus:outline-none focus:border-[#2F6E14] disabled:opacity-50 resize-y" />

        {state === 'error' && errorMessage && (
          <div role="alert" className="mt-4 rounded-md px-3 py-2.5 font-['Metropolis'] text-[12px]" style={{ backgroundColor: 'rgba(224,41,10,0.08)', border: '1px solid rgba(224,41,10,0.35)', color: '#E0290A' }}>
            {errorMessage}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={safeClose} disabled={submitting} className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-[#51534A] hover:text-fusion-mid-grey bg-transparent border-0 cursor-pointer disabled:opacity-50">Cancel</button>
          <button type="button" onClick={submit} disabled={!picked || submitting}
            className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-white bg-[#2F6E14] border border-[#2F6E14] rounded-full px-5 py-2 inline-flex items-center gap-2 transition-[filter,opacity] duration-150 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed">
            {submitting ? (<><Spinner /><span>Sending…</span></>) : <span>{picked ? `Ask ${(picked.full_name || picked.email).split(' ')[0]}` : 'Ask'}</span>}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
